import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DataTableProps } from '../../../types'; // Adjust the import path as necessary
import { useNavigate } from 'react-router-dom';
import { formatTimestamp } from '../../../formatTimestamp';
import Icon from '@mdi/react';
import { mdiArrowUp, mdiArrowDown } from '@mdi/js';
import './DataTable.scss';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';

interface ExtendedDataTableProps extends DataTableProps {
  selectedIds: Set<number>;
  onSelectionChange: (selectedIds: Set<number>) => void;
}

const DataTable: React.FC<ExtendedDataTableProps> = ({ 
  data, 
  onSort, 
  hasMore, 
  fetchData, 
  selectedIds, 
  onSelectionChange,
  sortBy = 'dateCreated',
  sortDirection = 'desc'
}) => {

    const navigate = useNavigate();

    const [selectedRow, setSelectedRow] = useState<number>(-1);
    // Function to handle row click
    const handleRowClick = (id: number) => {
      navigate(`/account-requests/details/${id}`);
      setSelectedRow(id);
    };

    // Function to handle checkbox selection
    const handleCheckboxChange = (id: number, event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const newSelectedIds = new Set(selectedIds);
      
      if (newSelectedIds.has(id)) {
        newSelectedIds.delete(id);
      } else {
        newSelectedIds.add(id);
      }
      
      onSelectionChange(newSelectedIds);
    };

    // Function to handle select all checkbox
    const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSelectedIds = new Set<number>();
      
      if (event.target.checked) {
        data.forEach(item => newSelectedIds.add(item.id));
      }
      
      onSelectionChange(newSelectedIds);
    };

    // Check if all visible rows are selected
    const isAllSelected = data.length > 0 && data.every(item => selectedIds.has(item.id));
    const isIndeterminate = data.some(item => selectedIds.has(item.id)) && !isAllSelected;

    // Helper function to render sort indicators
    const getSortIndicator = (columnName: string) => {
        // Show indicator only if this column is currently sorted
        if (sortBy !== columnName) {
            return null; // No indicator for unsorted columns
        }
        
        return (
            <Icon 
                path={sortDirection === 'asc' ? mdiArrowUp : mdiArrowDown} 
                size={0.6} 
                className="sort-indicator"
            />
        );
    };

    // Helper function to get sort class for styling
    const getSortClass = (columnName: string) => {
        if (sortBy === columnName) {
            return `sortable sorted-${sortDirection}`;
        }
        return 'sortable';
    };

    const getAppStatusClass = (status: string | null | undefined) => {
      if (!status) return 'pending';
      
      switch (status.toLowerCase()) {
        case 'complete':
          return 'completed';
        case 'pending':
          return 'pending';
        case 'declined':
          return 'declined';
        case 'voided':
          return 'voided';
        default:
          return 'pending';
      }
    };

    const formatPhoneNumber = (phone: string | null | undefined) => {
      if (!phone) return null;
      
      // Remove all non-digit characters
      const cleaned = phone.replace(/\D/g, '');
      
      // Handle different lengths - assume all are US numbers
      if (cleaned.length === 10) {
        // Format as (XXX) XXX-XXXX
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
        // Remove leading 1 and format as (XXX) XXX-XXXX
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      } else if (cleaned.length >= 10) {
        // Take last 10 digits and format as (XXX) XXX-XXXX
        const last10 = cleaned.slice(-10);
        return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
      } else {
        // Return original if less than 10 digits (incomplete number)
        return phone;
      }
    };

  return (
    <div className='data-table-wrapper' id='table-scroll-body'>
    <InfiniteScroll
      dataLength={data.length}
      next={fetchData}
      hasMore={hasMore}
      loader={
        <div className='table-loader'><LoadingIndicator /></div>
      }
      scrollableTarget="scrollable-content"
    >
      <table className='data-table'>
        <thead>
          <tr>
            <th className="checkbox-column">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(el) => {
                  if (el) el.indeterminate = isIndeterminate;
                }}
                onChange={handleSelectAllChange}
                title={isAllSelected ? "Deselect all" : "Select all"}
              />
            </th>
            <th className={getSortClass('id')} onClick={() => onSort('id')}>
              <span className="header-content">id</span>
              {getSortIndicator('id')}
            </th>
            <th className={getSortClass('accountNumber')} onClick={() => onSort('accountNumber')}>
              <span className="header-content">Account #</span>
              {getSortIndicator('accountNumber')}
            </th>
            <th className={getSortClass('lastName')} onClick={() => onSort('lastName')}>
              <span className="header-content">Acct Name</span>
              {getSortIndicator('lastName')}
            </th>
            <th className={getSortClass('toEmail')} onClick={() => onSort('toEmail')}>
              <span className="header-content">Email</span>
              {getSortIndicator('toEmail')}
            </th>
            <th className={getSortClass('toPhone')} onClick={() => onSort('toPhone')}>
              <span className="header-content">Phone</span>
              {getSortIndicator('toPhone')}
            </th>
            <th className={getSortClass('type')} onClick={() => onSort('type')}>
              <span className="header-content">Type</span>
              {getSortIndicator('type')}
            </th>
            <th className={getSortClass('status')} onClick={() => onSort('status')}>
              <span className="header-content">Plaid</span>
              {getSortIndicator('status')}
            </th>
            <th className={getSortClass('appStatus')} onClick={() => onSort('appStatus')}>
              <span className="header-content">DocuSign</span>
              {getSortIndicator('appStatus')}
            </th>
            <th className={getSortClass('referralSource')} onClick={() => onSort('referralSource')}>
              <span className="header-content">Source</span>
              {getSortIndicator('referralSource')}
            </th>
            <th className={getSortClass('externalSource')} onClick={() => onSort('externalSource')}>
              <span className="header-content">App Type</span>
              {getSortIndicator('externalSource')}
            </th>
            <th className={getSortClass('dateCreated')} onClick={() => onSort('dateCreated')}>
              <span className="header-content">Created</span>
              {getSortIndicator('dateCreated')}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr 
              key={item.id} 
              className={`${selectedRow === item.id ? 'selected' : ''} ${selectedIds.has(item.id) ? 'row-selected' : ''}`} 
              onClick={() => handleRowClick(item.id)}
            >
              <td className="checkbox-column" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(item.id)}
                  onChange={(e) => handleCheckboxChange(item.id, e)}
                />
              </td>
              <td title={item.id.toString()}>{item.id}</td>
              <td title={item.accountNumber || ''}>{item.accountNumber}</td>
              <td title={item.accountType === 'business' && item.businessName ? item.businessName : `${item.lastName}, ${item.firstName}`}>
                {item.accountType === 'business' && item.businessName ? item.businessName : `${item.lastName}, ${item.firstName}`}
              </td>
              <td title={item.toEmail || ''}>{item.toEmail}</td>
              <td title={item.toPhone || 'No phone number'}>
                {formatPhoneNumber(item.toPhone) ? (
                  formatPhoneNumber(item.toPhone)
                ) : (
                  <span className="faded-null">—</span>
                )}
              </td>
              <td title={item.type || ''}>{item.type}</td>
              <td title={item.status || ''}><span className={'tag-' + item.status}>{item.status}</span></td>
              <td title={item.appStatus || 'No app status'}>
                {item.appStatus ? (
                  <span className={`tag-${getAppStatusClass(item.appStatus)}`}>
                    {item.appStatus}
                  </span>
                ) : (
                  <span className="faded-null">—</span>
                )}
              </td>
              <td title={item.referralSource || 'No referral source'}>
                <span className={!item.referralSource ? 'faded-null' : ''}>
                  {item.referralSource || '—'}
                </span>
              </td>
              <td title={item.externalSource || 'No account type'}><span className={!item.externalSource ? 'faded-null' : ''}>{item.externalSource || '—'}</span></td>
              <td title={new Date(item.dateCreated).toLocaleString()}>{formatTimestamp(item.dateCreated.toString())}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </InfiniteScroll>
    </div>
  );
};

export default DataTable;

